import React, { useState } from "react";
import { sendEmailVerification } from "firebase/auth";
import { registerUser } from "../firebaseAuth.js";
import { educationAuth } from "../firebase.js";

function Inscription() {
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [age, setAge] = useState("");
  const [profession, setProfession] = useState("");
  const [residence, setResidence] = useState("");

  // ✅ Téléphone
  const [countryCode, setCountryCode] = useState("+33");
  const [telephone, setTelephone] = useState("");

  // ✅ Photo optionnelle
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setPhotoFile(null);
      setPhotoPreview("");
      return;
    }

    // Sécurité : seulement images
    if (!file.type.startsWith("image/")) {
      setMessage("❌ Veuillez sélectionner un fichier image (PNG/JPG/WebP).");
      e.target.value = "";
      return;
    }

    // Limite (ex: 2 Mo)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setMessage("❌ Image trop lourde (max 2 Mo).");
      e.target.value = "";
      return;
    }

    setMessage("");
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setPrenom("");
    setNom("");
    setAge("");
    setProfession("");
    setResidence("");
    setCountryCode("+33");
    setTelephone("");
    setEmail("");
    setPassword("");
    setPhotoFile(null);
    setPhotoPreview("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    try {
      const fullPhone = `${(countryCode || "").trim()} ${(telephone || "").trim()}`.trim();

      // On garde ta logique actuelle
      const result = await registerUser(email, password, {
        prenom: prenom.trim(),
        nom: nom.trim(),
        age: age ? Number(age) : null,
        profession: profession.trim(),
        residence: residence.trim(),
        telephone: fullPhone,
        countryCode: (countryCode || "").trim(),
        photoFile: photoFile || null,
      });

      // registerUser renvoie directement l'utilisateur Éducation créé.
      const createdUser = result || educationAuth.currentUser;

      // ✅ Envoi du mail de vérification
      if (createdUser && !createdUser.emailVerified) {
        await sendEmailVerification(createdUser);
      }

      setMessage(
        "✅ Compte créé avec succès ! Un email de vérification vous a été envoyé. Veuillez confirmer votre adresse email avant de vous connecter."
      );

      resetForm();
    } catch (error) {
      if (error?.code === "auth/email-already-in-use") {
        setMessage(
          "❌ Cette adresse e-mail est déjà utilisée. Connectez-vous ou utilisez une autre adresse."
        );
      } else if (error?.code === "auth/invalid-email") {
        setMessage("❌ Adresse e-mail invalide.");
      } else if (error?.code === "auth/weak-password") {
        setMessage("❌ Mot de passe trop faible. Minimum 6 caractères.");
      } else {
        setMessage("❌ Erreur : " + (error?.message || "Une erreur est survenue"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Créer un compte</h2>
          <p style={styles.subtitle}>
            Renseignez vos informations pour créer votre profil.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Prénom</label>
              <input
                placeholder="Ex: Hady"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Nom</label>
              <input
                placeholder="Ex: Barry"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Âge</label>
              <input
                type="number"
                placeholder="Ex: 25"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                style={styles.input}
                min="1"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Profession</label>
              <input
                placeholder="Ex: Étudiant / Salarié"
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldFull}>
              <label style={styles.label}>Lieu de résidence</label>
              <input
                placeholder="Ex: Marseille"
                value={residence}
                onChange={(e) => setResidence(e.target.value)}
                style={styles.input}
              />
            </div>

            {/* ✅ Photo optionnelle */}
            <div style={styles.fieldFull}>
              <label style={styles.label}>Photo de profil (optionnel)</label>

              <div style={styles.photoRow}>
                <div style={styles.avatar}>
                  {photoPreview ? (
                    <img src={photoPreview} alt="Aperçu" style={styles.avatarImg} />
                  ) : (
                    <span style={styles.avatarText}>
                      {(prenom || nom || "U").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={styles.fileInput}
                  />
                  <div style={styles.helpText}>PNG/JPG/WebP — max 2 Mo</div>
                </div>
              </div>
            </div>

            {/* ✅ Téléphone avec indicatif */}
            <div style={styles.field}>
              <label style={styles.label}>Indicatif pays</label>
              <input
                type="text"
                placeholder="Ex: +33"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Téléphone</label>
              <input
                type="tel"
                placeholder="Ex: 612345678"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.fieldFull}>
              <label style={styles.label}>Adresse e-mail</label>
              <input
                type="email"
                placeholder="exemple@mail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                required
              />
            </div>

            <div style={styles.fieldFull}>
              <label style={styles.label}>Mot de passe</label>
              <input
                type="password"
                placeholder="Minimum 6 caractères"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                required
              />
            </div>
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? "Création..." : "S’inscrire"}
          </button>

          {message && (
            <div
              style={{
                ...styles.alert,
                ...(message.startsWith("✅") ? styles.alertSuccess : styles.alertError),
              }}
            >
              {message}
            </div>
          )}
        </form>

        <div style={styles.footer}>
          <span style={styles.footerText}>Déjà un compte ?</span>{" "}
          <a href="/connexion" style={styles.link}>
            Se connecter
          </a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "calc(100vh - 140px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px 16px",
    background:
      "radial-gradient(1200px 600px at 20% 10%, rgba(74,108,247,0.18), transparent 60%), radial-gradient(900px 500px at 90% 30%, rgba(108,169,242,0.20), transparent 55%), #f7f9ff",
  },

  card: {
    width: "100%",
    maxWidth: 720,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(74,108,247,0.12)",
    borderRadius: 18,
    boxShadow: "0 18px 48px rgba(2,12,27,0.12)",
    padding: 24,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  },

  header: { marginBottom: 14, textAlign: "center" },
  title: { margin: 0, fontSize: 26, fontWeight: 900, color: "#0e1829" },
  subtitle: { margin: "8px 0 0", color: "#586274", fontSize: 14 },

  form: { display: "flex", flexDirection: "column", gap: 14 },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },

  field: { display: "flex", flexDirection: "column", gap: 6 },
  fieldFull: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    gridColumn: "1 / -1",
  },

  label: { fontSize: 13, color: "#0e1829", fontWeight: 700 },

  input: {
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid #d4ddee",
    outline: "none",
    fontSize: 14,
    background: "#fff",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
  },

  // ✅ Photo styles
  photoRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: 12,
    borderRadius: 12,
    border: "1px solid #e6ecff",
    background: "#f8faff",
  },
  avatar: {
    width: 54,
    height: 54,
    borderRadius: 16,
    border: "1px solid #d4ddee",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  avatarText: { fontWeight: 900, color: "#4a6cf7", fontSize: 18 },
  fileInput: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #d4ddee",
    background: "#fff",
  },
  helpText: { marginTop: 6, fontSize: 12, color: "#586274" },

  button: {
    marginTop: 6,
    padding: "12px 14px",
    borderRadius: 12,
    border: "none",
    fontWeight: 800,
    cursor: "pointer",
    color: "#fff",
    background: "linear-gradient(135deg, #4a6cf7, #6ca9f2)",
    boxShadow: "0 12px 24px rgba(74,108,247,0.28)",
  },

  alert: {
    marginTop: 4,
    padding: "10px 12px",
    borderRadius: 12,
    fontSize: 14,
    lineHeight: 1.4,
    border: "1px solid transparent",
    textAlign: "center",
  },
  alertSuccess: {
    background: "rgba(16,185,129,0.10)",
    color: "#0f5132",
    borderColor: "rgba(16,185,129,0.20)",
  },
  alertError: {
    background: "rgba(244,63,94,0.10)",
    color: "#7f1d1d",
    borderColor: "rgba(244,63,94,0.22)",
  },

  footer: { marginTop: 14, textAlign: "center", fontSize: 14 },
  footerText: { color: "#586274" },
  link: { color: "#4a6cf7", fontWeight: 800, textDecoration: "none" },
};

export default Inscription;